import { useEffect, useState, useRef } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useParams, useLocation, Link } from "wouter";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Pencil, Trash2, Phone, Mail, User, Building, ChevronRight, AlertCircle, AlertTriangle, ClipboardList, FileText, Upload, X, Download, Sparkles, CheckCircle2 } from "lucide-react";
import { differenceInDays, format } from "date-fns";
import type { Acquisition, AcquisitionStatus, AcquisitionDocument, DocumentType } from "@shared/schema";
import { ACQUISITION_STATUSES, LOAN_TYPES, LOAN_TYPE_LABELS, DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS } from "@shared/schema";
import { ClosingTeamModal } from "@/components/closing-team-modal";
import { ContractExtractionProgress } from "@/components/contract-extraction-progress";

const ACQUISITION_TRIGGER_STAGES: AcquisitionStatus[] = ["offer_submitted", "offer_accepted", "clear_to_close", "closed"];

const STATUS_LABELS: Record<AcquisitionStatus, string> = {
  prospect: "Prospect",
  offer_submitted: "Offer Submitted",
  offer_accepted: "Offer Accepted",
  under_contract: "Under Contract",
  due_diligence: "Due Diligence",
  appraisal: "Appraisal",
  clear_to_close: "Clear to Close",
  closing: "Closing",
  closed: "Closed",
};

const STATUS_COLORS: Record<AcquisitionStatus, string> = {
  prospect: "bg-gray-100 text-gray-700",
  offer_submitted: "bg-blue-100 text-blue-700",
  offer_accepted: "bg-indigo-100 text-indigo-700",
  under_contract: "bg-purple-100 text-purple-700",
  due_diligence: "bg-yellow-100 text-yellow-700",
  appraisal: "bg-orange-100 text-orange-700",
  clear_to_close: "bg-teal-100 text-teal-700",
  closing: "bg-emerald-100 text-emerald-700",
  closed: "bg-green-100 text-green-700",
};

function formatCurrency(val: string | null | undefined): string {
  if (!val) return "—";
  const n = parseFloat(val.replace(/[^0-9.]/g, ""));
  if (isNaN(n)) return val;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function formatDate(val: string | null | undefined): string {
  if (!val) return "—";
  try {
    return format(new Date(val), "MMM d, yyyy");
  } catch {
    return val;
  }
}

function formatPhone(val: string | null | undefined): string {
  if (!val) return "";
  const digits = val.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function PhoneInput({ value, onChange, testId }: { value: string; onChange: (v: string) => void; testId?: string }) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    onChange(digits);
  };
  return (
    <Input
      type="tel"
      value={formatPhone(value)}
      onChange={handleChange}
      placeholder="(555) 555-5555"
      className="text-sm"
      data-testid={testId}
    />
  );
}

function DeadlineCountdown({ date, label }: { date: string | null | undefined; label: string }) {
  if (!date) return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm text-gray-400">Not set</span>
    </div>
  );
  const days = differenceInDays(new Date(date), new Date());
  const isPast = days < 0;
  const isSoon = days >= 0 && days <= 7;
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700">{formatDate(date)}</span>
        {isPast ? (
          <Badge className="bg-red-100 text-red-700 border-0 text-xs">Past due</Badge>
        ) : isSoon ? (
          <Badge className="bg-orange-100 text-orange-700 border-0 text-xs">{days}d left</Badge>
        ) : (
          <span className="text-xs text-gray-400">{days}d</span>
        )}
      </div>
    </div>
  );
}

function StatusStepper({ currentStatus }: { currentStatus: AcquisitionStatus }) {
  const currentIdx = ACQUISITION_STATUSES.indexOf(currentStatus);
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-2" data-testid="status-stepper">
      {ACQUISITION_STATUSES.map((s, idx) => {
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={s} className="flex items-center">
            <div className={`flex flex-col items-center min-w-[80px]`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                isCompleted ? "bg-green-500 border-green-500 text-white" :
                isCurrent ? "bg-blue-600 border-blue-600 text-white" :
                "bg-white border-gray-300 text-gray-400"
              }`}>
                {isCompleted ? "✓" : idx + 1}
              </div>
              <span className={`text-[10px] text-center mt-1 leading-tight ${isCurrent ? "text-blue-600 font-semibold" : isCompleted ? "text-green-600" : "text-gray-400"}`}>
                {STATUS_LABELS[s]}
              </span>
            </div>
            {idx < ACQUISITION_STATUSES.length - 1 && (
              <div className={`flex-shrink-0 w-6 h-0.5 mb-4 ${isCompleted ? "bg-green-400" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ContactCard({ name, phone, email, title }: { name?: string | null; phone?: string | null; email?: string | null; title: string }) {
  if (!name && !phone && !email) return null;
  const displayPhone = formatPhone(phone);
  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <User className="h-4 w-4 text-gray-400" />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
      </div>
      {name && <p className="text-sm font-medium text-gray-900 mb-1">{name}</p>}
      {displayPhone && (
        <a href={`tel:${(phone || "").replace(/\D/g, "")}`} className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline mb-0.5">
          <Phone className="h-3.5 w-3.5" /> {displayPhone}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
          <Mail className="h-3.5 w-3.5" /> {email}
        </a>
      )}
    </div>
  );
}

type ContractFieldKey =
  | "offerPrice"
  | "earnestMoney"
  | "optionFee"
  | "offerDate"
  | "contractDate"
  | "optionPeriodExpiration"
  | "inspectionDeadline"
  | "appraisalDeadline"
  | "closingDateScheduled"
  | "agentName"
  | "agentPhone"
  | "agentEmail"
  | "titleCompanyName"
  | "sellerName";

const CONTRACT_FIELD_LABELS: Record<ContractFieldKey, string> = {
  offerPrice: "Offer / Purchase Price",
  earnestMoney: "Earnest Money",
  optionFee: "Option Fee",
  offerDate: "Offer Date",
  contractDate: "Contract Date",
  optionPeriodExpiration: "Option Period Expiration",
  inspectionDeadline: "Inspection Deadline",
  appraisalDeadline: "Appraisal Deadline",
  closingDateScheduled: "Closing Date (Scheduled)",
  agentName: "Buyer Agent Name",
  agentPhone: "Buyer Agent Phone",
  agentEmail: "Buyer Agent Email",
  titleCompanyName: "Title Company Name",
  sellerName: "Seller Name",
};

const CONTRACT_FIELD_KEYS: ContractFieldKey[] = Object.keys(CONTRACT_FIELD_LABELS) as ContractFieldKey[];
const DATE_FIELDS = new Set<ContractFieldKey>(["offerDate", "contractDate", "optionPeriodExpiration", "inspectionDeadline", "appraisalDeadline", "closingDateScheduled"]);
const PHONE_FIELDS = new Set<ContractFieldKey>(["agentPhone"]);
const CURRENCY_FIELDS = new Set<ContractFieldKey>(["offerPrice", "earnestMoney", "optionFee"]);

export function ContractReviewDialog({
  open,
  onClose,
  extracted,
  current,
  acquisitionId,
  pdfDataUrl,
  pdfFileName,
}: {
  open: boolean;
  onClose: () => void;
  extracted: Record<string, string | null>;
  current: Acquisition;
  acquisitionId: number;
  pdfDataUrl: string;
  pdfFileName: string;
}) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const { toast } = useToast();
  const ADDRESS_EXTRACT_MAP: Record<string, keyof Acquisition> = {
    propertyAddress: "address",
    propertyCity: "city",
    propertyState: "state",
    propertyZipCode: "zipCode",
  };
  const ADDRESS_LABELS: Record<string, string> = {
    propertyAddress: "Street Address",
    propertyCity: "City",
    propertyState: "State",
    propertyZipCode: "ZIP Code",
  };
  const ADDRESS_KEYS = ["propertyAddress", "propertyCity", "propertyState", "propertyZipCode"] as const;

  const normalizeForCompare = (s: string): string => {
    return s.trim().toLowerCase()
      .replace(/[.,#]/g, "")
      .replace(/\bstreet\b/g, "st")
      .replace(/\bavenue\b/g, "ave")
      .replace(/\bboulevard\b/g, "blvd")
      .replace(/\bdrive\b/g, "dr")
      .replace(/\blane\b/g, "ln")
      .replace(/\bcourt\b/g, "ct")
      .replace(/\broad\b/g, "rd")
      .replace(/\bcircle\b/g, "cir")
      .replace(/\bplace\b/g, "pl")
      .replace(/\bnorth\b/g, "n")
      .replace(/\bsouth\b/g, "s")
      .replace(/\beast\b/g, "e")
      .replace(/\bwest\b/g, "w")
      .replace(/\s+/g, " ");
  };

  const addressMismatches = ADDRESS_KEYS.filter((k) => {
    const extractedVal = extracted[k]?.trim();
    if (!extractedVal) return false;
    const currentVal = (current[ADDRESS_EXTRACT_MAP[k]] as string | null)?.trim() || "";
    if (!currentVal) return true;
    return normalizeForCompare(extractedVal) !== normalizeForCompare(currentVal);
  });

  const [acceptedAddress, setAcceptedAddress] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    ADDRESS_KEYS.forEach((k) => { init[k] = false; });
    return init;
  });

  const [accepted, setAccepted] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    CONTRACT_FIELD_KEYS.forEach((k) => {
      init[k] = extracted[k] != null && extracted[k] !== "";
    });
    return init;
  });

  const [editedValues, setEditedValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    CONTRACT_FIELD_KEYS.forEach((k) => {
      init[k] = extracted[k] || "";
    });
    ADDRESS_KEYS.forEach((k) => {
      init[k] = extracted[k] || "";
    });
    return init;
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const fields: Record<string, string | null> = {};
      CONTRACT_FIELD_KEYS.forEach((k) => {
        if (accepted[k]) {
          const val = editedValues[k] || null;
          fields[k] = PHONE_FIELDS.has(k) ? (val?.replace(/\D/g, "") || null) : val;
        }
      });
      ADDRESS_KEYS.forEach((k) => {
        if (acceptedAddress[k]) {
          fields[k] = editedValues[k] || null;
        }
      });

      await apiRequest("POST", `/api/app/acquisitions/${acquisitionId}/apply-contract`, {
        fields,
        pdfFileName,
        pdfDataUrl,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisitionId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisitionId, "documents"] });
      toast({ title: "Contract data saved and PDF stored" });
      onClose();
    },
    onError: () => {
      toast({ title: "Failed to save contract data", variant: "destructive" });
    },
  });

  const currentValue = (k: ContractFieldKey): string => {
    const val = current[k];
    if (!val) return "—";
    if (DATE_FIELDS.has(k)) return formatDate(val);
    if (PHONE_FIELDS.has(k)) return formatPhone(val);
    if (CURRENCY_FIELDS.has(k)) return formatCurrency(val);
    return val;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && saveMutation.isPending) return;
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-contract-review"
        onInteractOutside={(e) => {
          if (saveMutation.isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (saveMutation.isPending) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (saveMutation.isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500" />
            Review Extracted Contract Data
          </DialogTitle>
          <DialogDescription>
            Check the fields extracted by AI. Accept, edit, or skip each field before saving.
          </DialogDescription>
        </DialogHeader>

        {addressMismatches.length > 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="address-mismatch-banner">
            <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Address Mismatch Detected
            </div>
            <p className="text-xs text-amber-600">
              The address on the contract differs from the current acquisition address. Check each field below and accept to update.
            </p>
            <div className="space-y-1.5">
              {addressMismatches.map((k) => {
                const currentVal = (current[ADDRESS_EXTRACT_MAP[k]] as string | null) || "—";
                return (
                  <div key={k} className="grid grid-cols-[auto_1fr_1fr] gap-x-3 items-center p-1.5 rounded bg-white/60" data-testid={`address-mismatch-${k}`}>
                    <Checkbox
                      checked={acceptedAddress[k]}
                      onCheckedChange={(v) => setAcceptedAddress((a) => ({ ...a, [k]: !!v }))}
                      data-testid={`checkbox-address-${k}`}
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-600">{ADDRESS_LABELS[k]}</p>
                      {acceptedAddress[k] ? (
                        <Input
                          value={editedValues[k]}
                          onChange={(e) => setEditedValues((ev) => ({ ...ev, [k]: e.target.value }))}
                          className="text-sm h-7 mt-0.5"
                          data-testid={`input-address-${k}`}
                        />
                      ) : (
                        <p className="text-sm text-amber-800 font-medium">{extracted[k]}</p>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-400">Current</p>
                      <p className="text-sm text-gray-500 truncate">{currentVal}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-1 text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 pb-1 border-b">
            <span>Accept</span>
            <span>Field / Extracted Value</span>
            <span>Current Value</span>
          </div>
          {CONTRACT_FIELD_KEYS.map((k) => {
            const extractedVal = extracted[k];
            const hasValue = extractedVal != null && extractedVal !== "";
            const displayExtracted = hasValue
              ? DATE_FIELDS.has(k)
                ? formatDate(extractedVal!)
                : PHONE_FIELDS.has(k)
                ? formatPhone(extractedVal!)
                : CURRENCY_FIELDS.has(k)
                ? formatCurrency(extractedVal!)
                : extractedVal!
              : null;

            return (
              <div key={k} className={`grid grid-cols-[auto_1fr_1fr] gap-x-3 items-start p-2 rounded ${accepted[k] ? "bg-blue-50" : "opacity-60"}`} data-testid={`contract-field-${k}`}>
                <div className="pt-1">
                  <Checkbox
                    checked={accepted[k]}
                    onCheckedChange={(v) => setAccepted((a) => ({ ...a, [k]: !!v }))}
                    disabled={!hasValue}
                    data-testid={`checkbox-accept-${k}`}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-700 mb-0.5">{CONTRACT_FIELD_LABELS[k]}</p>
                  {hasValue ? (
                    accepted[k] ? (
                      PHONE_FIELDS.has(k) ? (
                        <PhoneInput
                          value={editedValues[k]}
                          onChange={(v) => setEditedValues((ev) => ({ ...ev, [k]: v }))}
                          testId={`input-extracted-${k}`}
                        />
                      ) : (
                        <Input
                          type={DATE_FIELDS.has(k) ? "date" : "text"}
                          value={editedValues[k]}
                          onChange={(e) => setEditedValues((ev) => ({ ...ev, [k]: e.target.value }))}
                          className="text-sm h-8"
                          data-testid={`input-extracted-${k}`}
                        />
                      )
                    ) : (
                      <p className="text-sm text-gray-600">{displayExtracted}</p>
                    )
                  ) : (
                    <p className="text-sm text-gray-400 italic">Not found</p>
                  )}
                </div>
                <div className="min-w-0 pt-5">
                  <p className="text-sm text-gray-500 truncate">{currentValue(k)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded p-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
          <span>Accepted fields will overwrite current values. The PDF will be saved as a contract document.</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="button-cancel-contract-review">Cancel</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={readOnly || (saveMutation.isPending)} data-testid="button-confirm-contract-save">
            {saveMutation.isPending ? "Saving..." : "Save Accepted Fields"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractUploadButton({ acquisitionId, acquisition }: { acquisitionId: number; acquisition: Acquisition }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [extracted, setExtracted] = useState<Record<string, string | null> | null>(null);
  const [pdfDataUrl, setPdfDataUrl] = useState("");
  const [pdfFileName, setPdfFileName] = useState("");

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf" || String(file.name ?? "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast({ title: "Only PDF files are supported", variant: "destructive" });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large (max 15MB)", variant: "destructive" });
      return;
    }

    setExtracting(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const base64 = dataUrl.split(",")[1];
      let data: { extracted?: Record<string, string | null>; message?: string };
      try {
        const res = await apiRequest(
          "POST",
          `/api/app/acquisitions/${acquisitionId}/extract-contract`,
          { pdfBase64: base64 },
          { signal: controller.signal },
        );
        data = await res.json();
      } catch (err: unknown) {
        if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
          return;
        }
        console.error("[ContractUploadButton] extract-contract failed:", err);
        if (!isMountedRef.current) return;
        toast({
          title: "AI extraction failed",
          description: parseErrorMessage(err, "Couldn't extract data from this contract. Please try again."),
          variant: "destructive",
        });
        return;
      }

      if (controller.signal.aborted || !isMountedRef.current) return;

      if (!data.extracted) {
        toast({ title: "AI extraction failed", description: data.message || "Couldn't extract data from this contract. Please try again.", variant: "destructive" });
        return;
      }

      setPdfDataUrl(dataUrl);
      setPdfFileName(file.name);
      setExtracted(data.extracted);
      setReviewOpen(true);
    } catch (err: unknown) {
      if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
        return;
      }
      console.error("[ContractUploadButton] contract upload failed:", err);
      if (!isMountedRef.current) return;
      toast({
        title: "Failed to extract contract data",
        description: parseErrorMessage(err, "Couldn't extract data from this contract. Please try again."),
        variant: "destructive",
      });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      if (isMountedRef.current) {
        setExtracting(false);
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".pdf,application/pdf"
        data-testid="input-contract-file"
      />
      <div className="space-y-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={extracting}
          className="border-blue-500 text-blue-700 hover:bg-blue-50"
          data-testid="button-upload-contract"
        >
          <Sparkles className="h-4 w-4 mr-1.5" />
          {extracting ? "Extracting…" : "Upload Purchase Contract"}
        </Button>
        {extracting && (
          <ContractExtractionProgress active={extracting} fileName={pdfFileName} variant="inline" />
        )}
      </div>
      {extracted && reviewOpen && (
        <ContractReviewDialog
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          extracted={extracted}
          current={acquisition}
          acquisitionId={acquisitionId}
          pdfDataUrl={pdfDataUrl}
          pdfFileName={pdfFileName}
        />
      )}
    </>
  );
}

// Task #3370 — confirm-before-save dialog for the AI-extracted closing-statement
// (HUD) commission. The host reviews/edits the realtor commission amount that
// will be billed at 25% as a referral invoice, then we POST apply-hud to store
// the PDF as a `closing_statement` document and persist the commission.
type HudExtracted = {
  realEstateCommission: string | null;
  buyerAgentCommission: string | null;
  realtorName: string | null;
};

function HudReviewDialog({
  open,
  onClose,
  onApplied,
  extracted,
  acquisitionId,
  pdfDataUrl,
  pdfFileName,
}: {
  open: boolean;
  onClose: () => void;
  onApplied?: () => void;
  extracted: HudExtracted;
  acquisitionId: number;
  pdfDataUrl: string;
  pdfFileName: string;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Default the commission basis to the buyer-agent commission when itemized,
  // otherwise the total real-estate commission.
  const [commission, setCommission] = useState<string>(
    extracted.buyerAgentCommission || extracted.realEstateCommission || "",
  );

  const applyMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/app/acquisitions/${acquisitionId}/apply-hud`, {
        commissionAmount: commission ? commission.replace(/[^0-9.]/g, "") : null,
        pdfFileName,
        pdfDataUrl,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisitionId, "documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisitionId] });
      toast({ title: "Closing statement saved" });
      onApplied?.();
      onClose();
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to save closing statement",
        description: parseErrorMessage(err, "Couldn't save the closing statement. Please try again."),
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && applyMutation.isPending) return;
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="max-w-md"
        data-testid="dialog-hud-review"
        onInteractOutside={(e) => { if (applyMutation.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (applyMutation.isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (applyMutation.isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand-accent" />
            Confirm Realtor Commission
          </DialogTitle>
          <DialogDescription>
            We extracted the commission from your closing statement. Confirm or edit the realtor commission below — PadPulse bills a referral fee of 25% of this amount.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md bg-gray-50 p-2.5">
              <p className="text-xs text-gray-500">Total Commission</p>
              <p className="font-medium text-gray-900" data-testid="text-hud-total-commission">
                {extracted.realEstateCommission ? formatCurrency(extracted.realEstateCommission) : "—"}
              </p>
            </div>
            <div className="rounded-md bg-gray-50 p-2.5">
              <p className="text-xs text-gray-500">Buyer Agent Commission</p>
              <p className="font-medium text-gray-900" data-testid="text-hud-buyer-commission">
                {extracted.buyerAgentCommission ? formatCurrency(extracted.buyerAgentCommission) : "—"}
              </p>
            </div>
          </div>
          {extracted.realtorName && (
            <div className="rounded-md bg-gray-50 p-2.5 text-sm">
              <p className="text-xs text-gray-500">Realtor (from statement)</p>
              <p className="font-medium text-gray-900" data-testid="text-hud-realtor-name">{extracted.realtorName}</p>
            </div>
          )}
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Realtor Commission to Bill *</Label>
            <Input
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
              data-testid="input-hud-commission"
            />
            <p className="text-xs text-gray-400 mt-1" data-testid="text-hud-referral-fee">
              Referral fee (25%): {commission && !isNaN(parseFloat(commission.replace(/[^0-9.]/g, ""))) ? formatCurrency(String(parseFloat(commission.replace(/[^0-9.]/g, "")) * 0.25)) : "—"}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={applyMutation.isPending}
            data-testid="button-hud-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => applyMutation.mutate()}
            disabled={applyMutation.isPending}
            data-testid="button-hud-save"
          >
            {applyMutation.isPending ? "Saving…" : "Save Closing Statement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HudUploadButton({
  acquisitionId,
  onApplied,
  variant = "outline",
  label = "Upload Closing Statement",
}: {
  acquisitionId: number;
  onApplied?: () => void;
  variant?: "outline" | "default";
  label?: string;
}) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [extracted, setExtracted] = useState<HudExtracted | null>(null);
  const [pdfDataUrl, setPdfDataUrl] = useState("");
  const [pdfFileName, setPdfFileName] = useState("");

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf" || String(file.name ?? "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast({ title: "Only PDF files are supported", variant: "destructive" });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large (max 15MB)", variant: "destructive" });
      return;
    }

    setExtracting(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const base64 = dataUrl.split(",")[1];
      let data: { extracted?: HudExtracted; message?: string };
      try {
        const res = await apiRequest(
          "POST",
          `/api/app/acquisitions/${acquisitionId}/extract-hud`,
          { pdfBase64: base64 },
          { signal: controller.signal },
        );
        data = await res.json();
      } catch (err: unknown) {
        if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
          return;
        }
        console.error("[HudUploadButton] extract-hud failed:", err);
        if (!isMountedRef.current) return;
        toast({
          title: "AI extraction failed",
          description: parseErrorMessage(err, "Couldn't extract data from this closing statement. Please try again."),
          variant: "destructive",
        });
        return;
      }

      if (controller.signal.aborted || !isMountedRef.current) return;

      if (!data.extracted) {
        toast({ title: "AI extraction failed", description: data.message || "Couldn't extract data from this closing statement. Please try again.", variant: "destructive" });
        return;
      }

      setPdfDataUrl(dataUrl);
      setPdfFileName(file.name);
      setExtracted(data.extracted);
      setReviewOpen(true);
    } catch (err: unknown) {
      if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
        return;
      }
      console.error("[HudUploadButton] closing statement upload failed:", err);
      if (!isMountedRef.current) return;
      toast({
        title: "Failed to extract closing statement data",
        description: parseErrorMessage(err, "Couldn't extract data from this closing statement. Please try again."),
        variant: "destructive",
      });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      if (isMountedRef.current) {
        setExtracting(false);
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".pdf,application/pdf"
        data-testid="input-hud-file"
      />
      <Button
        variant={variant}
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={extracting}
        className={variant === "outline" ? "border-brand-accent text-brand-accent hover:bg-brand-accent/10" : undefined}
        data-testid="button-upload-hud"
      >
        <Sparkles className="h-4 w-4 mr-1.5" />
        {extracting ? "Extracting…" : label}
      </Button>
      {extracting && (
        <ContractExtractionProgress active={extracting} fileName={pdfFileName} variant="inline" />
      )}
      {extracted && reviewOpen && (
        <HudReviewDialog
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          onApplied={onApplied}
          extracted={extracted}
          acquisitionId={acquisitionId}
          pdfDataUrl={pdfDataUrl}
          pdfFileName={pdfFileName}
        />
      )}
    </>
  );
}

function DocumentsSection({ acquisitionId }: { acquisitionId: number }) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState<DocumentType>("contract");
  const [docNotes, setDocNotes] = useState("");

  const docsQuery = useQuery<AcquisitionDocument[]>({
    queryKey: ["/api/app/acquisitions", acquisitionId, "documents"],
    queryFn: async () => {
      const res = await fetch(`/api/app/acquisitions/${acquisitionId}/documents`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (docId: number) => {
      return apiRequest("DELETE", `/api/app/acquisitions/${acquisitionId}/documents/${docId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisitionId, "documents"] });
      toast({ title: "Document deleted" });
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large (max 15MB)", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl: string = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await apiRequest("POST", `/api/app/acquisitions/${acquisitionId}/documents`, {
        fileName: file.name,
        fileUrl: dataUrl,
        documentType: docType,
        notes: docNotes || null,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisitionId, "documents"] });
      toast({ title: "Document uploaded" });
      setDocNotes("");
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const docs = docsQuery.data || [];
  const grouped = DOCUMENT_TYPES.reduce((acc, dt) => {
    const matching = docs.filter((d) => d.documentType === dt);
    if (matching.length > 0) acc.push({ type: dt, docs: matching });
    return acc;
  }, [] as { type: DocumentType; docs: AcquisitionDocument[] }[]);

  return (
    <Card data-testid="card-documents" data-tour="acquisition-detail-docs">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700">Documents</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 mb-4">
          <div className="flex-1">
            <Label className="text-xs text-gray-600">Document Type</Label>
            <Select value={docType} onValueChange={(v) => setDocType(v as DocumentType)}>
              <SelectTrigger className="text-sm" data-testid="select-doc-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((dt) => (
                  <SelectItem key={dt} value={dt}>{DOCUMENT_TYPE_LABELS[dt]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label className="text-xs text-gray-600">Notes (optional)</Label>
            <Input
              value={docNotes}
              onChange={(e) => setDocNotes(e.target.value)}
              placeholder="e.g., Amendment #2"
              className="text-sm"
              data-testid="input-doc-notes"
            />
          </div>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="button-upload-document"
            >
              <Upload className="h-4 w-4 mr-1" />
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </div>
        {docsQuery.isLoading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No documents uploaded yet</p>
        ) : (
          <div className="space-y-3">
            {grouped.map((group) => (
              <div key={group.type}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {DOCUMENT_TYPE_LABELS[group.type]}
                </p>
                <div className="space-y-1">
                  {group.docs.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 p-2 rounded border border-gray-100 hover:bg-gray-50" data-testid={`document-row-${doc.id}`}>
                      <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{doc.fileName}</p>
                        {doc.notes && <p className="text-xs text-gray-500 truncate">{doc.notes}</p>}
                        <p className="text-xs text-gray-400">{format(new Date(doc.uploadedAt), "MMM d, yyyy")}</p>
                      </div>
                      <a
                        href={`/api/app/acquisitions/${acquisitionId}/documents/${doc.id}/download`}
                        download={doc.fileName}
                        className="p-1 rounded hover:bg-gray-200 text-gray-500"
                        data-testid={`button-download-doc-${doc.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </a>
                      <button disabled={readOnly}
                        onClick={() => deleteMutation.mutate(doc.id)}
                        className="p-1 rounded hover:bg-red-100 text-red-500"
                        data-testid={`button-delete-doc-${doc.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EditAcquisitionDialog({ acquisition, open, onClose, focusField }: { acquisition: Acquisition; open: boolean; onClose: () => void; focusField?: string | null }) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const { toast } = useToast();
  const [form, setForm] = useState({
    address: acquisition.address || "",
    status: acquisition.status as AcquisitionStatus,
    purchasingEntity: acquisition.purchasingEntity || "",
    expectedInterestRate: acquisition.expectedInterestRate || "",
    loanType: acquisition.loanType || "",
    offerPrice: acquisition.offerPrice || "",
    earnestMoney: acquisition.earnestMoney || "",
    optionFee: acquisition.optionFee || "",
    closingCostsEstimate: acquisition.closingCostsEstimate || "",
    offerDate: acquisition.offerDate || "",
    offerExpirationDate: acquisition.offerExpirationDate || "",
    contractDate: acquisition.contractDate || "",
    optionPeriodExpiration: acquisition.optionPeriodExpiration || "",
    inspectionDeadline: acquisition.inspectionDeadline || "",
    appraisalDeadline: acquisition.appraisalDeadline || "",
    financingContingencyDeadline: acquisition.financingContingencyDeadline || "",
    closingDateScheduled: acquisition.closingDateScheduled || "",
    closingDateActual: acquisition.closingDateActual || "",
    agentName: acquisition.agentName || "",
    agentPhone: (acquisition.agentPhone || "").replace(/\D/g, ""),
    agentEmail: acquisition.agentEmail || "",
    lenderName: acquisition.lenderName || "",
    lenderPhone: (acquisition.lenderPhone || "").replace(/\D/g, ""),
    lenderEmail: acquisition.lenderEmail || "",
    titleCompanyName: acquisition.titleCompanyName || "",
    titleCompanyPhone: (acquisition.titleCompanyPhone || "").replace(/\D/g, ""),
    titleCompanyEmail: acquisition.titleCompanyEmail || "",
    sellerName: acquisition.sellerName || "",
    sellerPhone: (acquisition.sellerPhone || "").replace(/\D/g, ""),
    sellerEmail: acquisition.sellerEmail || "",
    notes: acquisition.notes || "",
    noRenovationNeeded: acquisition.noRenovationNeeded || false,
  });

  const [showClosePrompt, setShowClosePrompt] = useState(false);
  const [promptNoReno, setPromptNoReno] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !focusField) return;
    const timer = setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-testid="input-${focusField}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus({ preventScroll: true });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [open, focusField]);

  const updateMutation = useMutation({
    mutationFn: async (overrides?: Partial<typeof form>) => {
      const payload: Record<string, string | boolean | null> = {};
      const effectiveForm = { ...form, ...overrides };
      Object.entries(effectiveForm).forEach(([k, v]) => {
        payload[k] = v === "" ? null : v;
      });
      return apiRequest("PATCH", `/api/app/acquisitions/${acquisition.id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisition.id] });
      toast({ title: "Acquisition updated" });
      onClose();
    },
    onError: (error: unknown) => {
      toast({ title: parseErrorMessage(error, "Failed to update"), variant: "destructive" });
    },
  });

  const validateRequiredContacts = () => {
    const errors: Record<string, string> = {};
    if (!form.agentName.trim()) errors.agentName = "Realtor (Agent) Name is required";
    if (!form.lenderName.trim()) errors.lenderName = "Lender/Broker Name is required";
    if (!form.titleCompanyName.trim()) errors.titleCompanyName = "Title Company Name is required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateRequiredContacts()) return;
    if (form.status === "closed" && acquisition.status !== "closed") {
      setPromptNoReno(form.noRenovationNeeded);
      setShowClosePrompt(true);
    } else {
      updateMutation.mutate(undefined as any);
    }
  };

  const field = (label: string, key: keyof typeof form, type = "text", required = false) => {
    const errorMsg = fieldErrors[key as string];
    return (
      <div>
        <Label className="text-xs text-gray-600">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {type === "textarea" ? (
          <Textarea
            value={form[key] as string}
            onChange={(e) => {
              setForm((f) => ({ ...f, [key]: e.target.value }));
              if (fieldErrors[key as string]) setFieldErrors((fe) => ({ ...fe, [key as string]: "" }));
            }}
            className="text-sm"
            rows={3}
            data-testid={`input-${key}`}
          />
        ) : (
          <Input
            type={type}
            value={form[key] as string}
            onChange={(e) => {
              setForm((f) => ({ ...f, [key]: e.target.value }));
              if (fieldErrors[key as string]) setFieldErrors((fe) => ({ ...fe, [key as string]: "" }));
            }}
            className={`text-sm${errorMsg ? " border-red-500" : ""}`}
            aria-required={required}
            data-testid={`input-${key}`}
          />
        )}
        {errorMsg && <p className="text-xs text-red-500 mt-0.5">{errorMsg}</p>}
      </div>
    );
  };

  const phoneField = (label: string, key: "agentPhone" | "lenderPhone" | "titleCompanyPhone" | "sellerPhone") => (
    <div>
      <Label className="text-xs text-gray-600">{label}</Label>
      <PhoneInput
        value={form[key]}
        onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
        testId={`input-${key}`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-edit-acquisition">
        <DialogHeader>
          <DialogTitle>Edit Acquisition</DialogTitle>
          <DialogDescription className="sr-only">Update acquisition details</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">{field("Address", "address")}</div>
            <div>
              <Label className="text-xs text-gray-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v as AcquisitionStatus }))}>
                <SelectTrigger data-testid="select-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACQUISITION_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 mt-5">
              <Checkbox
                id="no-reno"
                checked={form.noRenovationNeeded}
                onCheckedChange={(v) => setForm((f) => ({ ...f, noRenovationNeeded: !!v }))}
                data-testid="checkbox-no-renovation"
              />
              <Label htmlFor="no-reno" className="text-sm cursor-pointer">No renovation needed (close → Active)</Label>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Deal Details</h3>
            <div className="grid grid-cols-3 gap-3">
              {field("Purchasing Entity", "purchasingEntity")}
              {field("Expected Interest Rate (%)", "expectedInterestRate")}
              <div>
                <Label className="text-xs text-gray-600">Loan Type</Label>
                <Select value={form.loanType || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, loanType: v === "__none" ? "" : v }))}>
                  <SelectTrigger data-testid="select-loan-type">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Select...</SelectItem>
                    {LOAN_TYPES.map((lt) => (
                      <SelectItem key={lt} value={lt}>{LOAN_TYPE_LABELS[lt]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Financials</h3>
            <div className="grid grid-cols-2 gap-3">
              {field("Offer Price", "offerPrice")}
              {field("Earnest Money", "earnestMoney")}
              {field("Option Fee", "optionFee")}
              {field("Closing Costs Est.", "closingCostsEstimate")}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Key Dates</h3>
            <div className="grid grid-cols-2 gap-3">
              {field("Offer Date", "offerDate", "date")}
              {field("Offer Expiration", "offerExpirationDate", "date")}
              {field("Contract Date", "contractDate", "date")}
              {field("Option Period Expiration", "optionPeriodExpiration", "date")}
              {field("Inspection Deadline", "inspectionDeadline", "date")}
              {field("Appraisal Deadline", "appraisalDeadline", "date")}
              {field("Financing Contingency", "financingContingencyDeadline", "date")}
              {field("Closing (Scheduled)", "closingDateScheduled", "date")}
              {field("Closing (Actual)", "closingDateActual", "date")}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Agent</h3>
            <div className="grid grid-cols-3 gap-3">
              {field("Name", "agentName", "text", true)}
              {phoneField("Phone", "agentPhone")}
              {field("Email", "agentEmail")}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Lender/Broker</h3>
            <div className="grid grid-cols-3 gap-3">
              {field("Name", "lenderName", "text", true)}
              {phoneField("Phone", "lenderPhone")}
              {field("Email", "lenderEmail")}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Title Company</h3>
            <div className="grid grid-cols-3 gap-3">
              {field("Name", "titleCompanyName", "text", true)}
              {phoneField("Phone", "titleCompanyPhone")}
              {field("Email", "titleCompanyEmail")}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Seller</h3>
            <div className="grid grid-cols-3 gap-3">
              {field("Name", "sellerName")}
              {phoneField("Phone", "sellerPhone")}
              {field("Email", "sellerEmail")}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
            {field("", "notes", "textarea")}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="button-cancel-edit">Cancel</Button>
          <Button onClick={handleSave} disabled={readOnly || updateMutation.isPending} data-testid="button-save-acquisition">
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={showClosePrompt} onOpenChange={(o) => { if (!o) setShowClosePrompt(false); }}>
        <DialogContent className="max-w-sm" data-testid="dialog-close-confirm">
          <DialogHeader>
            <DialogTitle>Close Acquisition</DialogTitle>
            <DialogDescription>
              Where should the linked property be moved in your workflow?
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Button
              variant={!promptNoReno ? "default" : "outline"}
              className={`flex flex-col h-auto py-4 border-2 ${!promptNoReno ? "border-orange-600 bg-orange-600 hover:bg-orange-700 text-white" : "hover:border-orange-500 hover:bg-orange-50"}`}
              onClick={() => setPromptNoReno(false)}
              data-testid="button-close-select-renovations"
            >
              <span className="font-semibold">Renovations</span>
              <span className={`text-xs mt-1 ${!promptNoReno ? "text-orange-100" : "text-gray-500"}`}>Property needs work</span>
            </Button>
            <Button
              variant={promptNoReno ? "default" : "outline"}
              className={`flex flex-col h-auto py-4 border-2 ${promptNoReno ? "border-green-600 bg-green-600 hover:bg-green-700 text-white" : "hover:border-green-500 hover:bg-green-50"}`}
              onClick={() => setPromptNoReno(true)}
              data-testid="button-close-select-active"
            >
              <span className="font-semibold">Active</span>
              <span className={`text-xs mt-1 ${promptNoReno ? "text-green-100" : "text-gray-500"}`}>No renovation needed</span>
            </Button>
          </div>
          <DialogFooter className="mt-2">
            <Button disabled={readOnly} variant="ghost" onClick={() => setShowClosePrompt(false)} data-testid="button-close-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => { setShowClosePrompt(false); updateMutation.mutate({ noRenovationNeeded: promptNoReno }); }}
              disabled={readOnly || (updateMutation.isPending)}
              data-testid="button-close-confirm"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export function ContractUploadPromptDialog({
  open,
  onClose,
  acquisitionId,
  acquisition,
}: {
  open: boolean;
  onClose: () => void;
  acquisitionId: number;
  acquisition: Acquisition;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const extractFileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [extracted, setExtracted] = useState<Record<string, string | null> | null>(null);
  const [pdfDataUrl, setPdfDataUrl] = useState("");
  const [pdfFileName, setPdfFileName] = useState("");

  const busy = uploading || extracting;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const docsQuery = useQuery<AcquisitionDocument[]>({
    queryKey: ["/api/app/acquisitions", acquisitionId, "documents"],
    queryFn: async () => {
      const res = await fetch(`/api/app/acquisitions/${acquisitionId}/documents`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: open,
  });

  const hasContract = docsQuery.data?.some((d) => d.documentType === "contract") ?? false;

  useEffect(() => {
    if (open && !docsQuery.isLoading && hasContract && !reviewOpen) {
      onClose();
    }
  }, [open, docsQuery.isLoading, hasContract, reviewOpen, onClose]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large (max 15MB)", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await apiRequest("POST", `/api/app/acquisitions/${acquisitionId}/documents`, {
        fileName: file.name,
        fileUrl: dataUrl,
        documentType: "contract",
        notes: "Purchase contract",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", acquisitionId, "documents"] });
      toast({ title: "Purchase contract uploaded" });
      onClose();
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleExtractFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf" || String(file.name ?? "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast({ title: "Only PDF files are supported", variant: "destructive" });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large (max 15MB)", variant: "destructive" });
      return;
    }

    setExtracting(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const base64 = dataUrl.split(",")[1];
      let data: { extracted?: Record<string, string | null>; message?: string };
      try {
        const res = await apiRequest(
          "POST",
          `/api/app/acquisitions/${acquisitionId}/extract-contract`,
          { pdfBase64: base64 },
          { signal: controller.signal },
        );
        data = await res.json();
      } catch (err: unknown) {
        if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
          return;
        }
        console.error("[ContractUploadPromptDialog] extract-contract failed:", err);
        if (!isMountedRef.current) return;
        toast({
          title: "AI extraction failed",
          description: parseErrorMessage(err, "Couldn't extract data from this contract. Please try again."),
          variant: "destructive",
        });
        return;
      }

      if (controller.signal.aborted || !isMountedRef.current) return;

      if (!data.extracted) {
        toast({ title: "AI extraction failed", description: data.message || "Couldn't extract data from this contract. Please try again.", variant: "destructive" });
        return;
      }

      setPdfDataUrl(dataUrl);
      setPdfFileName(file.name);
      setExtracted(data.extracted);
      setReviewOpen(true);
    } catch (err: unknown) {
      if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
        return;
      }
      console.error("[ContractUploadPromptDialog] contract upload failed:", err);
      if (!isMountedRef.current) return;
      toast({
        title: "Failed to extract contract data",
        description: parseErrorMessage(err, "Couldn't extract data from this contract. Please try again."),
        variant: "destructive",
      });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      if (isMountedRef.current) {
        setExtracting(false);
      }
      if (extractFileInputRef.current) extractFileInputRef.current.value = "";
    }
  };

  return (
    <>
      <Dialog
        open={open && !reviewOpen}
        onOpenChange={(next) => {
          if (!next && busy) return;
          if (!next) onClose();
        }}
      >
        <DialogContent
          className="max-w-sm"
          data-testid="dialog-contract-upload-prompt"
          onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-500" />
              Upload Purchase Contract
            </DialogTitle>
            <DialogDescription>
              This acquisition is now Under Contract. Upload the purchase contract and let AI pull key fields automatically, or upload it as-is. You can also skip and do this later from the Documents section.
            </DialogDescription>
          </DialogHeader>
          <input
            type="file"
            ref={extractFileInputRef}
            onChange={handleExtractFileChange}
            className="hidden"
            accept=".pdf,application/pdf"
            data-testid="input-contract-upload-prompt-extract-file"
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept=".pdf,application/pdf"
            data-testid="input-contract-upload-prompt-file"
          />
          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={() => extractFileInputRef.current?.click()}
              disabled={busy}
              className="w-full"
              data-testid="button-contract-upload-prompt-extract"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {extracting ? "Extracting…" : "Upload & Extract Fields"}
            </Button>
            {extracting && (
              <ContractExtractionProgress active={extracting} fileName={pdfFileName} variant="inline" />
            )}
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="w-full"
              data-testid="button-contract-upload-prompt-upload"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? "Uploading…" : "Upload without extraction"}
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={busy}
              className="w-full text-gray-500"
              data-testid="button-contract-upload-prompt-skip"
            >
              Skip for now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {extracted && reviewOpen && (
        <ContractReviewDialog
          open={reviewOpen}
          onClose={() => {
            setReviewOpen(false);
            onClose();
          }}
          extracted={extracted}
          current={acquisition}
          acquisitionId={acquisitionId}
          pdfDataUrl={pdfDataUrl}
          pdfFileName={pdfFileName}
        />
      )}
    </>
  );
}

export default function AppAcquisitionDetail() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [editFocusField, setEditFocusField] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const openEdit = (focusField?: string) => {
    setEditFocusField(focusField ?? null);
    setEditOpen(true);
  };

  const { data: acquisition, isLoading } = useQuery<Acquisition>({
    queryKey: ["/api/app/acquisitions", parseInt(params.id)],
    queryFn: async () => {
      const res = await fetch(`/api/app/acquisitions/${params.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
  });
  usePageTitle(acquisition?.address || "Acquisition");

  const [advanceClosePromptOpen, setAdvanceClosePromptOpen] = useState(false);
  const [advanceCloseNoReno, setAdvanceCloseNoReno] = useState<boolean>(false);
  const [closingTeamModalOpen, setClosingTeamModalOpen] = useState(false);
  const [pendingAdvanceStatus, setPendingAdvanceStatus] = useState<AcquisitionStatus | null>(null);
  const [contractUploadPromptOpen, setContractUploadPromptOpen] = useState(false);

  const { data: existingClosingTeam } = useQuery({
    queryKey: ["/api/app/referrals/closing-team/property", acquisition?.propertyId],
    enabled: !!acquisition?.propertyId,
  });

  // Task #3588 — pre-flight check for the advance-close dialog. Fetches
  // documents for the current acquisition so the dialog can surface the
  // amber callout before hitting the server gate.
  const { data: closeDialogDocs } = useQuery<AcquisitionDocument[]>({
    queryKey: ["/api/app/acquisitions", parseInt(params.id), "documents"],
    queryFn: async () => {
      const res = await fetch(`/api/app/acquisitions/${params.id}/documents`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch documents");
      return res.json();
    },
    enabled: !!acquisition,
  });
  const hasClosingStatement = (closeDialogDocs ?? []).some((d) => d.documentType === "closing_statement");

  const reminderMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/app/acquisitions/${params.id}/closing-statement-reminder`);
    },
    onSuccess: () => {
      // Auto-close the acquisition immediately after setting the reminder so
      // the host doesn't need to click Confirm separately (bypass the HUD gate
      // since they've explicitly acknowledged the missing document).
      advanceMutation.mutate({
        status: "closed",
        noRenovationNeeded: advanceCloseNoReno,
        bypassClosingStatement: true,
      });
    },
    onError: (error: unknown) => {
      toast({ title: parseErrorMessage(error, "Failed to set reminder"), variant: "destructive" });
    },
  });

  const advanceMutation = useMutation({
    mutationFn: async (payload: { status: AcquisitionStatus; noRenovationNeeded?: boolean; bypassClosingStatement?: boolean }) => {
      return apiRequest("PATCH", `/api/app/acquisitions/${params.id}`, payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", parseInt(params.id)] });
      if (variables.status === "closed" && acquisition?.propertyId) {
        const propertyId = acquisition.propertyId;
        if (variables.noRenovationNeeded) {
          toast({ title: "Property moved to Active — opening property page" });
          navigate(`/app/properties/${propertyId}`);
        } else {
          toast({ title: "Property moved to Renovations — opening renovation tracker" });
          navigate(`/app/renovations/${propertyId}`);
        }
      } else if (variables.status === "under_contract") {
        toast({ title: "Status updated" });
        const acqId = parseInt(params.id);
        queryClient.fetchQuery<AcquisitionDocument[]>({
          queryKey: ["/api/app/acquisitions", acqId, "documents"],
          queryFn: async () => {
            const res = await fetch(`/api/app/acquisitions/${acqId}/documents`, { credentials: "include" });
            if (!res.ok) throw new Error("Failed to fetch");
            return res.json();
          },
        }).then((docs) => {
          const hasContract = docs.some((d) => d.documentType === "contract");
          if (!hasContract) {
            setContractUploadPromptOpen(true);
          }
        }).catch(() => {
          setContractUploadPromptOpen(true);
        });
      } else {
        toast({ title: "Status updated" });
      }
    },
    onError: (error: unknown) => {
      toast({ title: parseErrorMessage(error, "Failed to update status"), variant: "destructive" });
    },
  });

  const proceedWithAdvance = (status: AcquisitionStatus, noRenovationNeeded?: boolean) => {
    advanceMutation.mutate({ status, noRenovationNeeded });
  };

  const handleAdvanceClick = () => {
    if (!nextStatus) return;

    const shouldShowModal = ACQUISITION_TRIGGER_STAGES.includes(nextStatus) && !existingClosingTeam && acquisition?.propertyId;

    if (nextStatus === "closed") {
      setAdvanceCloseNoReno(acquisition?.noRenovationNeeded ?? false);
      if (shouldShowModal) {
        setPendingAdvanceStatus("closed");
        setClosingTeamModalOpen(true);
      } else {
        setAdvanceClosePromptOpen(true);
      }
    } else if (shouldShowModal) {
      setPendingAdvanceStatus(nextStatus);
      setClosingTeamModalOpen(true);
    } else {
      advanceMutation.mutate({ status: nextStatus });
    }
  };

  const handleClosingTeamSaved = () => {
    setClosingTeamModalOpen(false);
    if (!pendingAdvanceStatus) return;
    if (pendingAdvanceStatus === "closed") {
      setAdvanceClosePromptOpen(true);
    } else {
      proceedWithAdvance(pendingAdvanceStatus);
    }
    setPendingAdvanceStatus(null);
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("DELETE", `/api/app/acquisitions/${params.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      navigate("/app/acquisitions");
      toast({ title: "Acquisition deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete", variant: "destructive" });
    },
  });

  const startUnderwritingMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/acquisitions/${params.id}/create-underwriting`);
      return res.json() as Promise<{ propertyId: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions", parseInt(params.id)] });
      toast({ title: "Opening underwriting analysis" });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => {
      toast({ title: "Failed to create underwriting", variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6">
          <div className="h-8 w-48 bg-gray-100 rounded animate-pulse mb-4" />
          <div className="h-32 bg-gray-100 rounded animate-pulse" />
        </div>
      </AppLayout>
    );
  }

  if (!acquisition) {
    return (
      <AppLayout>
        <div className="p-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-red-400 mb-3" />
          <p className="text-gray-600">Acquisition not found</p>
          <Button className="mt-4" asChild><Link href="/app/acquisitions">Back to Acquisitions</Link></Button>
        </div>
      </AppLayout>
    );
  }

  const currentIdx = ACQUISITION_STATUSES.indexOf(acquisition.status as AcquisitionStatus);
  const nextStatus = currentIdx < ACQUISITION_STATUSES.length - 1 ? ACQUISITION_STATUSES[currentIdx + 1] : null;

  const hasContacts = acquisition.agentName || acquisition.lenderName || acquisition.titleCompanyName || acquisition.sellerName;
  const hasDealDetails = acquisition.purchasingEntity || acquisition.loanType || acquisition.expectedInterestRate;

  const UNDER_CONTRACT_REQUIRED_FIELDS: { key: "agentName" | "lenderName" | "titleCompanyName"; label: string }[] = [
    { key: "agentName", label: "Realtor Name" },
    { key: "lenderName", label: "Lender/Broker Name" },
    { key: "titleCompanyName", label: "Title Company Name" },
  ];
  // Mirror the server's CLOSING_REQUIRED_STATUSES (server/routes/acquisitions.ts):
  // these same three fields are required to enter Under Contract or any later
  // closing stage, so the inline hint must appear for all of them.
  const CLOSING_REQUIRED_STATUSES = new Set<AcquisitionStatus>([
    "under_contract", "due_diligence", "appraisal",
    "clear_to_close", "closing", "closed",
  ]);
  const nextStatusIsClosing = nextStatus != null && CLOSING_REQUIRED_STATUSES.has(nextStatus);
  const missingUnderContractFields = nextStatusIsClosing
    ? UNDER_CONTRACT_REQUIRED_FIELDS.filter((f) => !(acquisition[f.key] || "").trim())
    : [];

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="page-acquisition-detail">
        <ViewOnlyBanner area="properties" />
        <div className="flex items-center gap-3 mb-6">
          <Link
            href="/app/acquisitions"
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 transition-colors"
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900" data-testid="text-acquisition-address" data-tour="acquisition-detail-heading">{acquisition.address}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`${STATUS_COLORS[acquisition.status as AcquisitionStatus]} border-0`} data-testid="badge-status">
                {STATUS_LABELS[acquisition.status as AcquisitionStatus] || acquisition.status}
              </Badge>
              {acquisition.offerPrice && (
                <span className="text-sm text-gray-500">{formatCurrency(acquisition.offerPrice)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <ContractUploadButton acquisitionId={parseInt(params.id)} acquisition={acquisition} />
            <HudUploadButton acquisitionId={parseInt(params.id)} />
            {acquisition.propertyId ? (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="button-view-underwriting"
              >
                <Link href={`/app/underwriting/${acquisition.propertyId}`}><ClipboardList className="h-4 w-4 mr-1.5" /> View Underwriting</Link>
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => startUnderwritingMutation.mutate()}
                disabled={readOnly || (startUnderwritingMutation.isPending)}
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="button-start-underwriting"
              >
                <ClipboardList className="h-4 w-4 mr-1.5" /> {startUnderwritingMutation.isPending ? "Creating..." : "Underwrite"}
              </Button>
            )}
            {nextStatus && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAdvanceClick}
                disabled={readOnly || advanceMutation.isPending}
                data-testid="button-advance-status"
              >
                Move to {STATUS_LABELS[nextStatus]} <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            <Button disabled={readOnly} variant="outline" size="sm" onClick={() => openEdit()} data-testid="button-edit-acquisition">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button disabled={readOnly} variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="text-red-600 hover:text-red-700" data-testid="button-delete-acquisition">
              <Trash2 className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="acquisition-detail" />
              <GuideButton pageKey="acquisition-detail" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="acquisition-detail" />
        </div>

        {!readOnly && nextStatusIsClosing && missingUnderContractFields.length > 0 && (
          <div
            className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4"
            data-testid="callout-missing-under-contract-fields"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  Add these before moving to {nextStatus ? STATUS_LABELS[nextStatus] : "the next stage"}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  The following required {missingUnderContractFields.length === 1 ? "field is" : "fields are"} still empty:
                </p>
                <ul className="mt-2 space-y-1">
                  {missingUnderContractFields.map((f) => (
                    <li key={f.key} className="flex items-center justify-between gap-2">
                      <span className="text-sm text-amber-900" data-testid={`text-missing-field-${f.key}`}>
                        • {f.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => openEdit(f.key)}
                        className="text-xs font-medium text-brand hover:underline flex-shrink-0"
                        data-testid={`button-fill-in-${f.key}`}
                      >
                        Fill in
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <Card className="mb-6" data-testid="card-status-progress" data-tour="acquisition-detail-progress">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-gray-600">Pipeline Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusStepper currentStatus={acquisition.status as AcquisitionStatus} />
          </CardContent>
        </Card>

        {hasDealDetails && (
          <Card className="mb-4" data-testid="card-deal-details">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">Deal Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {acquisition.purchasingEntity && (
                  <div>
                    <p className="text-xs text-gray-500">Purchasing Entity</p>
                    <p className="text-sm font-medium text-gray-900" data-testid="text-purchasing-entity">{acquisition.purchasingEntity}</p>
                  </div>
                )}
                {acquisition.loanType && (
                  <div>
                    <p className="text-xs text-gray-500">Loan Type</p>
                    <p className="text-sm font-medium text-gray-900" data-testid="text-loan-type">
                      {LOAN_TYPE_LABELS[acquisition.loanType as keyof typeof LOAN_TYPE_LABELS] || acquisition.loanType}
                    </p>
                  </div>
                )}
                {acquisition.expectedInterestRate && (
                  <div>
                    <p className="text-xs text-gray-500">Expected Interest Rate</p>
                    <p className="text-sm font-medium text-gray-900" data-testid="text-interest-rate">{acquisition.expectedInterestRate}%</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Card data-testid="card-key-dates" data-tour="acquisition-detail-dates">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">Key Dates & Deadlines</CardTitle>
            </CardHeader>
            <CardContent>
              <DeadlineCountdown date={acquisition.offerDate} label="Offer Date" />
              <DeadlineCountdown date={acquisition.offerExpirationDate} label="Offer Expiration" />
              <DeadlineCountdown date={acquisition.contractDate} label="Contract Date" />
              <DeadlineCountdown date={acquisition.optionPeriodExpiration} label="Option Period Expiration" />
              <DeadlineCountdown date={acquisition.inspectionDeadline} label="Inspection Deadline" />
              <DeadlineCountdown date={acquisition.appraisalDeadline} label="Appraisal Deadline" />
              <DeadlineCountdown date={acquisition.financingContingencyDeadline} label="Financing Contingency" />
              <DeadlineCountdown date={acquisition.closingDateScheduled} label="Closing (Scheduled)" />
              <DeadlineCountdown date={acquisition.closingDateActual} label="Closing (Actual)" />
            </CardContent>
          </Card>

          <Card data-testid="card-financials" data-tour="acquisition-detail-financials">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">Financial Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-1.5 border-b">
                  <span className="text-sm text-gray-600">Offer Price</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(acquisition.offerPrice)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b">
                  <span className="text-sm text-gray-600">Earnest Money</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(acquisition.earnestMoney)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b">
                  <span className="text-sm text-gray-600">Option Fee</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(acquisition.optionFee)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b">
                  <span className="text-sm text-gray-600">Closing Costs Est.</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(acquisition.closingCostsEstimate)}</span>
                </div>
                {acquisition.expectedInterestRate && (
                  <div className="flex justify-between items-center py-1.5 border-b">
                    <span className="text-sm text-gray-600">Expected Interest Rate</span>
                    <span className="text-sm font-semibold text-gray-900">{acquisition.expectedInterestRate}%</span>
                  </div>
                )}
                {(acquisition.offerPrice && acquisition.closingCostsEstimate) && (() => {
                  const offer = parseFloat(acquisition.offerPrice!.replace(/[^0-9.]/g, ""));
                  const costs = parseFloat(acquisition.closingCostsEstimate!.replace(/[^0-9.]/g, ""));
                  const optFee = acquisition.optionFee ? parseFloat(acquisition.optionFee.replace(/[^0-9.]/g, "")) : 0;
                  const total = offer + costs + (isNaN(optFee) ? 0 : optFee);
                  return !isNaN(offer) && !isNaN(costs) ? (
                    <div className="flex justify-between items-center py-1.5 bg-gray-50 rounded px-2">
                      <span className="text-sm font-medium text-gray-700">Total Est. Cost</span>
                      <span className="text-sm font-bold text-gray-900">
                        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(total)}
                      </span>
                    </div>
                  ) : null;
                })()}
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox checked={!!acquisition.noRenovationNeeded} disabled />
                  <span className="text-sm text-gray-600">No renovation needed (close → Active)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {hasContacts && (
          <Card className="mb-4" data-testid="card-contacts">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">Contacts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ContactCard title="Agent" name={acquisition.agentName} phone={acquisition.agentPhone} email={acquisition.agentEmail} />
                <ContactCard title="Lender/Broker" name={acquisition.lenderName} phone={acquisition.lenderPhone} email={acquisition.lenderEmail} />
                <ContactCard title="Title Company" name={acquisition.titleCompanyName} phone={acquisition.titleCompanyPhone} email={acquisition.titleCompanyEmail} />
                <ContactCard title="Seller" name={acquisition.sellerName} phone={acquisition.sellerPhone} email={acquisition.sellerEmail} />
              </div>
            </CardContent>
          </Card>
        )}

        {acquisition.notes && (
          <Card className="mb-4" data-testid="card-notes">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{acquisition.notes}</p>
            </CardContent>
          </Card>
        )}

        <div className="mb-4">
          <DocumentsSection acquisitionId={parseInt(params.id)} />
        </div>

        {!acquisition.notes && !hasContacts && !hasDealDetails && (
          <Card className="border-dashed" data-testid="card-empty-state">
            <CardContent className="p-6 text-center">
              <Building className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No additional details yet</p>
              <Button disabled={readOnly} variant="outline" size="sm" className="mt-3" onClick={() => openEdit()} data-testid="button-add-details">
                Add Details
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {editOpen && (
        <EditAcquisitionDialog
          acquisition={acquisition}
          open={editOpen}
          onClose={() => { setEditOpen(false); setEditFocusField(null); }}
          focusField={editFocusField}
        />
      )}

      {/* Task #3588 — gate dismissal on both mutations so a stray Esc or backdrop
          click can't drop an in-flight reminder + advance pair mid-flight. */}
      <Dialog
        open={advanceClosePromptOpen}
        onOpenChange={(o) => {
          if (!o && !reminderMutation.isPending && !advanceMutation.isPending) {
            setAdvanceClosePromptOpen(false);
          }
        }}
      >
        <DialogContent
          className="max-w-sm"
          data-testid="dialog-advance-close-confirm"
          onEscapeKeyDown={(e) => { if (reminderMutation.isPending || advanceMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (reminderMutation.isPending || advanceMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (reminderMutation.isPending || advanceMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Close Acquisition</DialogTitle>
            <DialogDescription>
              Where should the linked property be moved in your workflow?
            </DialogDescription>
          </DialogHeader>
          {/* Task #3588 — pre-flight callout: surface missing closing statement
              before the host hits the server gate so they can act inline.
              "Upload Now" closes the dialog and scrolls to the Documents card.
              "Remind me in 7 days" sets the reminder AND immediately closes the
              acquisition (single-click bypass so the host isn't blocked). */}
          {!hasClosingStatement && (
            <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30" data-testid="alert-missing-closing-statement">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <p className="font-medium mb-2">No closing statement (HUD/ALTA) on file.</p>
                <p className="text-sm mb-3">Upload it now to keep your records complete, or set a reminder and close anyway.</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200"
                    disabled={reminderMutation.isPending || advanceMutation.isPending}
                    onClick={() => {
                      setAdvanceClosePromptOpen(false);
                      requestAnimationFrame(() => {
                        document.querySelector("[data-testid='card-documents']")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      });
                    }}
                    data-testid="button-advance-close-upload-now"
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Upload Now
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200"
                    disabled={reminderMutation.isPending || advanceMutation.isPending}
                    onClick={() => reminderMutation.mutate()}
                    data-testid="button-advance-close-remind-later"
                  >
                    Remind me in 7 days
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-2 gap-3 py-2">
            <Button
              variant={!advanceCloseNoReno ? "default" : "outline"}
              className={`flex flex-col h-auto py-4 border-2 ${!advanceCloseNoReno ? "border-orange-600 bg-orange-600 hover:bg-orange-700 text-white" : "hover:border-orange-500 hover:bg-orange-50"}`}
              onClick={() => setAdvanceCloseNoReno(false)}
              disabled={reminderMutation.isPending || advanceMutation.isPending}
              data-testid="button-advance-close-select-renovations"
            >
              <span className="font-semibold">Renovations</span>
              <span className={`text-xs mt-1 ${!advanceCloseNoReno ? "text-orange-100" : "text-gray-500"}`}>Property needs work</span>
            </Button>
            <Button
              variant={advanceCloseNoReno ? "default" : "outline"}
              className={`flex flex-col h-auto py-4 border-2 ${advanceCloseNoReno ? "border-green-600 bg-green-600 hover:bg-green-700 text-white" : "hover:border-green-500 hover:bg-green-50"}`}
              onClick={() => setAdvanceCloseNoReno(true)}
              disabled={reminderMutation.isPending || advanceMutation.isPending}
              data-testid="button-advance-close-select-active"
            >
              <span className="font-semibold">Active</span>
              <span className={`text-xs mt-1 ${advanceCloseNoReno ? "text-green-100" : "text-gray-500"}`}>No renovation needed</span>
            </Button>
          </div>
          <DialogFooter className="mt-2">
            <Button
              variant="ghost"
              onClick={() => setAdvanceClosePromptOpen(false)}
              disabled={reminderMutation.isPending || advanceMutation.isPending}
              data-testid="button-advance-close-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setAdvanceClosePromptOpen(false);
                advanceMutation.mutate({ status: "closed", noRenovationNeeded: advanceCloseNoReno });
              }}
              disabled={readOnly || reminderMutation.isPending || advanceMutation.isPending || !hasClosingStatement}
              data-testid="button-advance-close-confirm"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="dialog-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Acquisition</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this acquisition? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={readOnly}
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMutation.mutate()}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ClosingTeamModal
        open={closingTeamModalOpen}
        propertyId={acquisition?.propertyId}
        acquisitionId={acquisition ? parseInt(params.id) : undefined}
        capturedAtStage={pendingAdvanceStatus || "offer_submitted"}
        stageLabel={pendingAdvanceStatus ? STATUS_LABELS[pendingAdvanceStatus] : ""}
        propertyAddress={acquisition?.address}
        onSuccess={handleClosingTeamSaved}
      />

      <ContractUploadPromptDialog
        open={contractUploadPromptOpen}
        onClose={() => setContractUploadPromptOpen(false)}
        acquisitionId={parseInt(params.id)}
        acquisition={acquisition}
      />
    </AppLayout>
  );
}
